import { apiFetch, ApiError } from './client'
import {
  settingsResponseSchema,
  settingsValidationSchema,
  testKhResultSchema,
  type SettingsResponse,
  type TestKhResult,
} from '@/lib/schemas/settings'

/**
 * Settings API (Screen 6). GET the full descriptor set, PUT only the changed
 * keys (changes take effect immediately server-side), and POST a KH connection
 * test. KH credentials are never sent here — they live in the server's ENV.
 */

/**
 * Thrown when PUT /api/settings returns a 400 VALIDATION body. `errors` maps a
 * setting key to a human message the UI renders under the matching input.
 */
export class SettingsValidationError extends Error {
  errors: Record<string, string>
  constructor(errors: Record<string, string>, message = 'Some settings are invalid.') {
    super(message)
    this.name = 'SettingsValidationError'
    this.errors = errors
  }
}

/** Pull a human message from a { detail } body in either shape the backend uses. */
function messageFromBody(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined
  const detail = (body as { detail?: unknown }).detail
  if (typeof detail === 'string') return detail
  if (detail && typeof detail === 'object') {
    const message = (detail as Record<string, unknown>).message
    if (typeof message === 'string') return message
  }
  return undefined
}

/** GET /api/settings — sections/fields rendered generically by the page. */
export async function getSettings(): Promise<SettingsResponse> {
  const res = await apiFetch('/api/settings', { method: 'GET' })
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    const detail = messageFromBody(body)
    throw new ApiError(res.status, detail ?? `Failed to load settings (${res.status})`, detail)
  }
  return settingsResponseSchema.parse(await res.json())
}

/**
 * PUT /api/settings — send ONLY the changed keys. Returns the full refreshed
 * settings shape on success; throws SettingsValidationError on a 400 VALIDATION.
 */
export async function updateSettings(changes: Record<string, string>): Promise<SettingsResponse> {
  const res = await apiFetch('/api/settings', {
    method: 'PUT',
    body: JSON.stringify({ changes }),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    if (res.status === 400) {
      const parsed = settingsValidationSchema.safeParse(body)
      if (parsed.success) throw new SettingsValidationError(parsed.data.detail.errors)
    }
    const detail = messageFromBody(body)
    throw new ApiError(res.status, detail ?? `Failed to save settings (${res.status})`, detail)
  }
  return settingsResponseSchema.parse(await res.json())
}

/** POST /api/settings/test-kh — probe the KH API with the ENV credentials. */
export async function testKh(): Promise<TestKhResult> {
  const res = await apiFetch('/api/settings/test-kh', { method: 'POST' })
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    const detail = messageFromBody(body)
    throw new ApiError(res.status, detail ?? `Connection test failed (${res.status})`, detail)
  }
  return testKhResultSchema.parse(await res.json())
}
